import { RadioButtonTw } from '@hqo/react-components-library/dist/atoms/radioButton';
import { usePaymentMethodsSelector } from 'components/payment/quick-checkout/components/payment-methods-selector/hooks/usePaymentMethodsSelector.hook';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useSelector, useDispatch } from 'react-redux';
import { APPLE_PAY, CREDITS } from 'shared/consts/payment-method-types';

import { setCurrentPaymentMethodId } from 'store/payment/actions';
import { selectCurrentPaymentMethodId, selectPaymentMethods } from 'store/payment/selectors';
import { SavedPaymentMethod } from 'store/payment/types';
import { CreditCycle } from 'store/price/types';
import { selectCart } from 'store/cart/selectors';

import { Title, ChangeButton, Header, PaymentMethodSelectorContainer } from './styles';

interface PaymentMethodManagerProps {
  onEditClick: VoidFunction;
  creditPrice?: number;
  availableCredit?: number;
  creditCycle?: CreditCycle;
  creditsAssignedUntil?: string;
}

export const PaymentMethodManager = ({
  onEditClick,
  creditPrice,
  availableCredit,
  creditCycle,
  creditsAssignedUntil,
}: PaymentMethodManagerProps) => {
  const intl = useIntl();

  const paymentMethods = useSelector(selectPaymentMethods);
  const cart = useSelector(selectCart);
  const dispatch = useDispatch();

  const hasCreditPrice = useMemo(() => {
    return cart?.items?.some(item => item.credit_price);
  }, [cart?.items]);

  const isCreditOnlyBooking = useMemo(() => {
    const hasCreditPricing = cart?.items?.some(item => item.credit_price);
    return cart?.total_summary?.total === 0 && hasCreditPricing;
  }, [cart?.items, cart?.total_summary?.total]);

  const filteredPaymentMethods = useMemo(() => {
    if (isCreditOnlyBooking) {
      const creditMethods = paymentMethods?.filter(method => method.payment_method_type === CREDITS) ?? [];
      if (creditMethods.length === 0 && creditPrice != null) {
        return [{ id: -99, payment_method_type: CREDITS } as SavedPaymentMethod];
      }
      return creditMethods;
    }
    if (hasCreditPrice) {
      return paymentMethods;
    }
    return paymentMethods?.filter(method => method.payment_method_type !== CREDITS);
  }, [paymentMethods, hasCreditPrice, isCreditOnlyBooking, creditPrice]);

  const options = usePaymentMethodsSelector(
    filteredPaymentMethods,
    creditPrice,
    availableCredit,
    creditCycle,
    creditsAssignedUntil,
  );
  const currentPaymentMethodId = useSelector(selectCurrentPaymentMethodId);

  const findCurrentPaymentMethod = useCallback(() => {
    const applePayMethod = filteredPaymentMethods?.find(
      paymentMethod => paymentMethod.payment_method_type === APPLE_PAY,
    );
    if (applePayMethod) {
      return applePayMethod;
    }

    if (currentPaymentMethodId) {
      const found = filteredPaymentMethods?.find(({ id }) => `${id}` === currentPaymentMethodId);
      if (found) return found;
    }

    return filteredPaymentMethods?.length
      ? filteredPaymentMethods?.find(method => method.default) || filteredPaymentMethods[0]
      : null;
  }, [filteredPaymentMethods, currentPaymentMethodId]);

  const initialMethod = findCurrentPaymentMethod();
  const initialMethodOption = initialMethod ? options.find(opt => opt.value === initialMethod.id.toString()) : null;
  const firstAvailableOption = initialMethodOption?.disabled
    ? options.find(opt => !opt.disabled) ?? null
    : initialMethodOption;
  const initialSelectedValue = firstAvailableOption?.value ?? undefined;

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string>(initialSelectedValue);

  useEffect(() => {
    if (initialSelectedValue) {
      dispatch(setCurrentPaymentMethodId(initialSelectedValue));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onPress = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      const selectedButton = options.find(option => option.value === value);
      if (selectedButton && !selectedButton.disabled) {
        setSelectedPaymentMethod(selectedButton.value);
        dispatch(setCurrentPaymentMethodId(selectedButton.value));
      }
    },
    [setSelectedPaymentMethod, options, dispatch],
  );

  return (
    !!filteredPaymentMethods.length && (
      <PaymentMethodSelectorContainer>
        <Header>
          <Title>{intl.formatMessage({ id: 'payment_method' })}</Title>
          {!isCreditOnlyBooking && (
            <ChangeButton onClick={onEditClick}>{intl.formatMessage({ id: 'change' })}</ChangeButton>
          )}
        </Header>
        {options.map(option => (
          <RadioButtonTw
            key={option.value}
            value={option.value}
            text={option.label}
            subtitle={option.subtitle}
            icon={option.icon}
            disabled={option.disabled}
            selected={option.value === selectedPaymentMethod}
            onChange={onPress}
            name="payment-method"
          />
        ))}
      </PaymentMethodSelectorContainer>
    )
  );
};
